import { chatMarkdown } from "./chat-markdown.js";
import { memo } from "react";
import { jsx } from "react/jsx-runtime";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
//#region src/components/Markdown/Markdown.tsx
/**
* A link to one of Core's things, `curri://<type>/<ref>`: a file of Source
* (`source_document`), a Slack channel (`slack_channel`).
*/
const CURRI_LINK = /^curri:\/\/([a-z_]+)\/([^/?#\s]+)$/;
/** Keeps a link to one of Core's things, which is not a web address. */
const urlTransform = (url) => CURRI_LINK.test(url) ? url : defaultUrlTransform(url);
/** The type size of each `size`: its prose, and a step under it for code and tables. */
const SIZES = {
	base: {
		prose: "text-body-base leading-body-base",
		small: "text-body-small"
	},
	medium: {
		prose: "text-body-medium leading-body-medium",
		small: "text-body-base"
	}
};
/**
* Chat prose as markdown: GFM tables, strikethrough and lists, links that
* open in a new tab, and the design system's type and colours on each node.
* Bullet glyphs at the head of a line read as list items (`chatMarkdown`).
* A link to one of Core's things is the host's to draw (`curriLink`).
*
* @param props.content - The markdown
* @param props.size - The prose's type size
* @param props.curriLink - Draws a link to one of Core's things
* @returns The rendered prose
*/
const Markdown = memo(function Markdown({ content, curriLink, size = "base" }) {
	return /* @__PURE__ */ jsx("div", {
		className: `font-body ${SIZES[size].prose} text-text-primary [&_p]:my-xs [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_li_p]:my-0 [&_li]:my-0`,
		children: /* @__PURE__ */ jsx(ReactMarkdown, {
			remarkPlugins: [remarkGfm],
			urlTransform,
			components: {
				a: ({ node: _node, ...props }) => {
					const [, type, ref] = CURRI_LINK.exec(props.href ?? "") ?? [];
					if (type && ref) return curriLink ? curriLink({
						children: props.children,
						ref,
						type
					}) : /* @__PURE__ */ jsx("span", { children: props.children });
					return /* @__PURE__ */ jsx("a", {
						...props,
						target: "_blank",
						rel: "noopener noreferrer",
						className: "text-text-brand underline"
					});
				},
				table: ({ node: _node, ...props }) => /* @__PURE__ */ jsx("div", {
					className: "my-xs overflow-x-auto rounded-xs border border-(length:--border-width-default) border-border-primary",
					style: { backgroundColor: "color-mix(in oklab, var(--color-elevation-surface-primary) 94%, white)" },
					children: /* @__PURE__ */ jsx("table", {
						...props,
						className: `w-full min-w-0 border-collapse ${SIZES[size].small}`
					})
				}),
				thead: ({ node: _node, ...props }) => /* @__PURE__ */ jsx("thead", {
					...props,
					className: "border-b border-(length:--border-width-default) border-border-primary text-left",
					style: { backgroundColor: "color-mix(in oklab, var(--color-elevation-surface-primary) 88%, white)" }
				}),
				th: ({ node: _node, ...props }) => /* @__PURE__ */ jsx("th", {
					...props,
					className: "px-xs py-xxs font-heading-semi-bold text-text-primary"
				}),
				td: ({ node: _node, ...props }) => /* @__PURE__ */ jsx("td", {
					...props,
					className: "border-t border-(length:--border-width-default) border-border-primary px-xs py-xxs align-top"
				}),
				ul: ({ node: _node, ...props }) => /* @__PURE__ */ jsx("ul", {
					...props,
					className: "my-xs list-outside list-disc pl-lg"
				}),
				ol: ({ node: _node, ...props }) => /* @__PURE__ */ jsx("ol", {
					...props,
					className: "my-xs list-outside list-decimal pl-lg"
				}),
				code: ({ node: _node, ...props }) => /* @__PURE__ */ jsx("code", {
					...props,
					className: `rounded-xs bg-elevation-surface-sunken px-xxs py-[1px] font-mono ${SIZES[size].small}`
				})
			},
			children: chatMarkdown(content)
		})
	});
});
//#endregion
export { Markdown };

//# sourceMappingURL=Markdown.js.map